import { chacha20poly1305 } from "@noble/ciphers/chacha.js";

const COUNTER_BYTES = 8;

/**
 * One direction-pair of an established, authenticated session.
 * Wire frame = counter (8 bytes, big-endian) || ChaCha20-Poly1305 ciphertext.
 * The counter doubles as the nonce, so a key is never reused with the same nonce,
 * and the receiver rejects any counter that is not strictly greater than the last
 * one it accepted (replay / reorder protection).
 */
export class SecureChannel {
  private sendCounter = 0;
  private lastReceived = -1;

  constructor(
    private readonly sendKey: Uint8Array,
    private readonly receiveKey: Uint8Array,
  ) {}

  encrypt(plaintext: Uint8Array): Uint8Array {
    if (this.sendCounter >= Number.MAX_SAFE_INTEGER) throw new Error("channel exhausted");
    const counter = this.sendCounter++;
    const nonce = nonceFor(counter);
    const ciphertext = chacha20poly1305(this.sendKey, nonce).encrypt(plaintext);
    const frame = new Uint8Array(COUNTER_BYTES + ciphertext.length);
    new DataView(frame.buffer).setBigUint64(0, BigInt(counter));
    frame.set(ciphertext, COUNTER_BYTES);
    return frame;
  }

  /** Throws on a tampered, forged, replayed or out-of-order frame. */
  decrypt(frame: Uint8Array): Uint8Array {
    if (frame.length < COUNTER_BYTES + 16) throw new Error("frame too short");
    const big = new DataView(frame.buffer, frame.byteOffset, frame.byteLength).getBigUint64(0);
    if (big > BigInt(Number.MAX_SAFE_INTEGER)) throw new Error("bad counter");
    const counter = Number(big);
    if (counter <= this.lastReceived) throw new Error("replayed or out-of-order frame");
    const plaintext = chacha20poly1305(this.receiveKey, nonceFor(counter)).decrypt(
      frame.subarray(COUNTER_BYTES),
    );
    // Only advance after authentication succeeds, so garbage can't burn counters.
    this.lastReceived = counter;
    return plaintext;
  }
}

function nonceFor(counter: number): Uint8Array {
  const nonce = new Uint8Array(12); // 4 zero bytes || 8-byte counter
  new DataView(nonce.buffer).setBigUint64(4, BigInt(counter));
  return nonce;
}
